import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import Layout from '@components/layout/Layout';
import MModalError from '@components/common/m-modal-error/MModalError.jsx';
import MModalSuccess from '@components/common/m-modal-success/MModalSuccess.jsx';
import RequireAuth, { RequireUnAuth } from '@components/app/PrivateRoute.jsx';

import { rolesList } from '@models/users.js';

import './App.css';
import ErrorBoundary from '@components/app/ErrorBoundary.jsx';

const Publications = lazy(() => import('./views/Publications/Publications'));
const PublicationContent = lazy(() => import('./views/PublicationContent/PublicationContent'));
const PublicationCreate = lazy(() => import('./views/PublicationCreate/PublicationCreate'));
const PublicationView = lazy(() => import('./views/PublicationView/PublicationView'));
const PublicationEdit = lazy(() => import('./views/PublicationEdit/PublicationEdit'));
const PublicationParagraphEdit = lazy(() => import('./views/PublicationParagraphEdit/PublicationParagraphEdit'));
const PublicationParagraphTaskCollection = lazy(() => import('./views/PublicationParagraphTaskCollection/PublicationParagraphTaskCollection'));
const Users = lazy(() => import('./views/Users/Users'));
const CreateUser = lazy(() => import('./views/CreateUser/CreateUser'));
const ViewUser = lazy(() => import('./views/Users/ViewUser'));
const ConfirmationReset = lazy(() => import('./views/confirmation/ConfirmationReset'));
const PassChange = lazy(() => import('./views/passChange/PassChange'));
const MainPage = lazy(() => import('./views/MainPage/MainPage'));
const UserEdit = lazy(() => import('./views/Users/UserEdit'));
const Settings = lazy(() => import('./views/Settings/Settings'));
const PublicationContentView = lazy(() => import('./views/PublicationContentView/PublicationContentView'));
const PublicationParagraphView = lazy(() => import('./views/PublicationParagraphView/PublicationParagraphView'));
const PublicationGlossary = lazy(() => import('./views/PublicationGlossary/PublicationGlossary'));

function App() {
    return (
      <ErrorBoundary>
          <Suspense fallback={
              <Layout/>
          }>
              <Routes>
                  <Route path='/' element={<Layout/>}>
                      <Route path='publications' element={
                          <RequireAuth>
                              <Publications/>
                          </RequireAuth>
                      }/>

                      <Route index element={
                          <RequireUnAuth>
                              <MainPage/>
                          </RequireUnAuth>
                      }/>

                      <Route path='publication/create' element={
                          <RequireAuth>
                              <PublicationCreate/>
                          </RequireAuth>
                      }/>

                      <Route path='publication/view/:id' element={
                          <RequireAuth>
                              <PublicationView/>
                          </RequireAuth>
                      }/>

                      <Route path='publication/content/:id' element={
                          <RequireAuth
                            roles={[rolesList.SUPERADMIN, rolesList.ADMIN, rolesList.EDITOR, rolesList.MAINEDITOR]}>
                              <PublicationContent/>
                          </RequireAuth>
                      }/>

                      <Route path='publication/content/:id/view' element={
                          <RequireAuth>
                              <PublicationContentView/>
                          </RequireAuth>
                      }/>

                      <Route path='publication/edit/:id' element={
                          <RequireAuth
                            roles={[rolesList.SUPERADMIN, rolesList.ADMIN, rolesList.EDITOR, rolesList.MAINEDITOR]}>
                              <PublicationEdit/>
                          </RequireAuth>
                      }/>

                      <Route path='publication/content/:bookId/paragraph/:id/edit' element={
                          <RequireAuth
                            roles={[rolesList.SUPERADMIN, rolesList.ADMIN, rolesList.EDITOR, rolesList.MAINEDITOR]}>
                              <PublicationParagraphEdit/>
                          </RequireAuth>
                      }/>

                      <Route path='publication/content/:bookId/paragraph/:id/view' element={
                          <RequireAuth>
                              <PublicationParagraphView/>
                          </RequireAuth>
                      }/>

                      <Route path='publication/content/:bookId/paragraph/:id/taskCollection' element={
                          <RequireAuth
                            roles={[rolesList.SUPERADMIN, rolesList.ADMIN, rolesList.EDITOR, rolesList.MAINEDITOR]}>
                              <PublicationParagraphTaskCollection/>
                          </RequireAuth>
                      }/>

                      <Route path='publication/content/:bookId/glossary' element={
                          <RequireAuth
                            roles={[rolesList.SUPERADMIN, rolesList.ADMIN, rolesList.EDITOR, rolesList.MAINEDITOR]}>
                              <PublicationGlossary/>
                          </RequireAuth>
                      }/>

                      <Route path='users' element={
                          <RequireAuth roles={[rolesList.ADMIN, rolesList.SUPERADMIN]}>
                              <Users/>
                          </RequireAuth>
                      }/>

                      <Route path='users/create' element={
                          <RequireAuth roles={[rolesList.ADMIN, rolesList.SUPERADMIN]}>
                              <CreateUser/>
                          </RequireAuth>
                      }/>

                      <Route path='user/:editorId' element={
                          <RequireAuth roles={[rolesList.ADMIN, rolesList.SUPERADMIN]}>
                              <ViewUser/>
                          </RequireAuth>
                      }/>

                      <Route path='user/edit/:editorId' element={
                          <RequireAuth roles={[rolesList.ADMIN, rolesList.SUPERADMIN]}>
                              <UserEdit/>
                          </RequireAuth>
                      }/>

                      <Route path='confirmation-reset' element={
                          <ConfirmationReset/>
                      }/>

                      <Route path='change-password' element={
                          <RequireAuth>
                              <PassChange/>
                          </RequireAuth>
                      }/>

                      <Route path='settings' element={
                          <RequireAuth>
                              <Settings/>
                          </RequireAuth>
                      }/>

                      <Route path='*' element={
                          <RequireUnAuth>
                              <MainPage/>
                          </RequireUnAuth>
                      }/>
                  </Route>
              </Routes>

              <MModalError/>

              <MModalSuccess/>
          </Suspense>
      </ErrorBoundary>
    );
}

export default App;
